import React, { FC, useState, useCallback, useMemo, useContext } from 'react'

import {
  EditorState,
  RichUtils,
  Modifier,
  EntityInstance,
} from 'draft-js'

import {
  IconComponentType,
} from '../../types/app'

import {
  getSelectionEntity,
  getEntityRange,
} from 'draftjs-utils'

import EditorContext from './EditorContext'

import {
  IData_MagicTag_Type,
} from '@kaidam/fc-shared/src/types/schema/magic_tag'

import {
  ITextEditor_Link,
  ITextEditor_EditorUpdate,
  ITextEditor_Entity_Link,
  ITextEditor_Link_Type,
} from '@kaidam/fc-shared/src/types/texteditor'

import {
  TEXT_EDITOR_ENTITY_TYPE_LINK,
} from '@kaidam/fc-shared/src/constants'

import ToolbarButton from './ToolbarButton'
import LinkEditor from './LinkEditor'

interface ToolbarButtonLinkProps {
  title: string,
  enabledMagicTags?: IData_MagicTag_Type[],
  publicFiles?: boolean,
  sharedFiles?: boolean,
  linkTypes?: ITextEditor_Link_Type[],
  className?: string,
  icon: IconComponentType,
  editorState: EditorState,
  setEditorState: ITextEditor_EditorUpdate,
}

const ToolbarButtonLink: FC<React.PropsWithChildren<ToolbarButtonLinkProps>> = ({
  title,
  enabledMagicTags = [],
  linkTypes = [],
  publicFiles,
  sharedFiles,
  className,
  icon,
  editorState,
  setEditorState,
}) => {

  const editorContext = useContext(EditorContext)
  const [ editing, setEditing ] = useState(false)

  const currentLinkEntityKey = useMemo<string>(() => {
    const entity = getSelectionEntity(editorState)
    return entity || ''
  }, [
    editorState,
  ])

  const currentLinkValue = useMemo<ITextEditor_Link | null>(() => {
    if(!currentLinkEntityKey) return null
    const contentState = editorState.getCurrentContent()
    const currentEntity = contentState.getEntity(currentLinkEntityKey)
    if(!currentEntity) return null
    if(currentEntity.getType() != TEXT_EDITOR_ENTITY_TYPE_LINK) return null
    const data = currentEntity.getData() as ITextEditor_Entity_Link
    if(data.type) {
      return data
    }
    else {
      return null
    }
  }, [
    editorState,
    currentLinkEntityKey,
  ])

  const hasCurrentLink = useMemo<boolean>(() => {
    return currentLinkValue ? true : false
  }, [
    currentLinkValue,
  ])

  const buttonDisabled = useMemo<boolean>(() => {
    const selection = editorState.getSelection()
    if(selection.isCollapsed() && !hasCurrentLink) return true
    else return false
  }, [
    hasCurrentLink,
    editorState,
  ])

  const onClick = useCallback(() => {
    setEditing(true)
  }, [])

  const onCancel = useCallback(() => {
    setEditing(false)
    setTimeout(() => {
      editorContext.focus()
    }, 1)
  }, [])

  const onSave = useCallback((link: ITextEditor_Link) => {
    const contentState = editorState.getCurrentContent()
    // replace an existing link
    if(currentLinkEntityKey) {
      const replacedLinkContent = contentState.replaceEntityData(currentLinkEntityKey, link)
      const newEditorState = EditorState.set(editorState, { currentContent: replacedLinkContent })
      setEditorState(newEditorState)
    }
    // add a new link
    else {
      const contentStateWithEntity = contentState.createEntity(
        'LINK',
        'MUTABLE',
        link,
      )
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey()
      const newEditorState = EditorState.set(editorState, { currentContent: contentStateWithEntity })
      setEditorState(RichUtils.toggleLink(
        newEditorState,
        newEditorState.getSelection(),
        entityKey
      ))
    }
    onCancel()
  }, [
    currentLinkEntityKey,
    editorState,
    setEditorState,
  ])

  const onReset = useCallback(() => {
    if(!currentLinkEntityKey) return
    const {
      start,
      end,
    }: {
      start: number,
      end: number,
    } = getEntityRange(editorState, currentLinkEntityKey)
    const contentState = editorState.getCurrentContent()
    const currentSelection = editorState.getSelection()
    const entitySelection = currentSelection.merge({
      anchorOffset: start,
      focusOffset: end,
    })
    const removedEntityContent = Modifier.applyEntity(
      contentState,
      entitySelection,
      null,
    )
    const removedEntityEditor = EditorState.push(
      editorState,
      removedEntityContent,
      'apply-entity'
    )
    setEditorState(EditorState.forceSelection(
      removedEntityEditor,
      currentSelection,
    ))
    onCancel()
  }, [
    editorState,
    currentLinkEntityKey,
  ])

  return (
    <>
      <ToolbarButton
        title={ title }
        className={ className }
        icon={ icon }
        active={ hasCurrentLink }
        disabled={ buttonDisabled }
        onClick={ onClick }
      />
      {
        editing && (
          <LinkEditor
            value={ currentLinkValue || undefined }
            enabledMagicTags={ enabledMagicTags }
            linkTypes={ linkTypes }
            publicFiles={ publicFiles }
            sharedFiles={ sharedFiles }
            onSave={ onSave }
            onReset={ onReset }
            onCancel={ onCancel }
          />
        )
      }
    </>
  )
}

export default ToolbarButtonLink